import type { QuestionResponseSchema } from "@/hera-api/client/types.gen";

const SERVICE_TRANSFER_RE = /service\s*transfer/i;

type AnswerMap = Record<string, unknown> | null | undefined;

function answerTextIndicatesServiceTransfer(value: unknown): boolean {
  if (typeof value === "string") {
    return SERVICE_TRANSFER_RE.test(value);
  }
  if (Array.isArray(value)) {
    return value.some((v) => answerTextIndicatesServiceTransfer(v));
  }
  return false;
}

/** True when deregistration answers indicate the registrant chose service transfer. */
export function isServiceTransferDeregistration(
  questions: QuestionResponseSchema[] | undefined,
  answers: AnswerMap,
): boolean {
  if (!answers) return false;
  const deregQs = (questions ?? []).filter(
    (q) => q.question_type === "deregistration",
  );
  const ids =
    deregQs.length > 0 ? deregQs.map((q) => q.id) : Object.keys(answers);
  return ids.some((id) => answerTextIndicatesServiceTransfer(answers[id]));
}

function tagFromQuestionText(text: string): "OMT" | "AMS" | null {
  const m =
    text.match(/\[\s*(OMT|AMS)\s*\]/i) ?? text.match(/\(\s*(OMT|AMS)\s*\)/i);
  if (m) return m[1].toUpperCase() as "OMT" | "AMS";
  const normalized = text
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (/^AMS$/i.test(normalized)) return "AMS";
  if (/^OMT$/i.test(normalized)) return "OMT";
  if (/asset\s*manager/i.test(normalized)) return "AMS";
  return null;
}

const AMS_OMT_ANSWER_TYPES = new Set([
  "company_contact",
  "name_address_contact",
]);

export type CompanyContactAnswer = {
  companyName?: string;
  company_name?: string;
  name?: string;
  email?: string;
  phone?: string;
  party_id?: string;
  [key: string]: unknown;
};

/** Prefer deregistration [AMS] company_contact answer; fall back to registration [AMS]. */
export function getAmsCompanyContactAnswer(
  questions: QuestionResponseSchema[] | undefined,
  answers: AnswerMap,
  options?: { preferDeregistration?: boolean },
): { questionId: string; answer: CompanyContactAnswer } | null {
  if (!answers) return null;
  const preferDereg = options?.preferDeregistration !== false;
  let dereg: { questionId: string; answer: CompanyContactAnswer } | null = null;
  let reg: { questionId: string; answer: CompanyContactAnswer } | null = null;

  for (const q of questions ?? []) {
    if (!AMS_OMT_ANSWER_TYPES.has(q.answer_type)) continue;
    if (tagFromQuestionText(q.question) !== "AMS") continue;
    const raw = answers[q.id];
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const entry = {
      questionId: q.id,
      answer: raw as CompanyContactAnswer,
    };
    if (q.question_type === "deregistration") {
      dereg = entry;
    } else {
      reg = entry;
    }
  }
  if (preferDereg && dereg) return dereg;
  return reg ?? dereg;
}

export type RecipientContactPrefill = {
  name: string;
  email: string;
  company_name: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
};

function contactString(
  answer: CompanyContactAnswer,
  ...keys: string[]
): string {
  for (const key of keys) {
    const value = answer[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

export function recipientFromCompanyContact(
  answer: CompanyContactAnswer | null | undefined,
): RecipientContactPrefill {
  if (!answer) {
    return {
      name: "",
      email: "",
      company_name: "",
      phone: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipcode: "",
    };
  }
  const company = contactString(answer, "companyName", "company_name");
  const person = contactString(answer, "name");
  return {
    name: person || company,
    email: contactString(answer, "email"),
    company_name: company,
    phone: contactString(answer, "phone"),
    address1: contactString(answer, "address1", "address_1"),
    address2: contactString(answer, "address2", "address_2"),
    city: contactString(answer, "city"),
    state: contactString(answer, "state"),
    zipcode: contactString(answer, "zipcode", "zip", "postal_code"),
  };
}

/** Registration (non-deregistration) [AMS] question id, for writing the new license AMS. */
export function getRegistrationAmsQuestionId(
  questions: QuestionResponseSchema[] | undefined,
): string | null {
  for (const q of questions ?? []) {
    if (q.question_type === "deregistration") continue;
    if (!AMS_OMT_ANSWER_TYPES.has(q.answer_type)) continue;
    if (tagFromQuestionText(q.question) === "AMS") return q.id;
  }
  return null;
}
